/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import I18n from 'I18n';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Component } from 'react';
import classNames from 'classnames';
import { isPageInKeyboardMode } from '../listeners/focusStylesListener';
import { getComponentPropType } from '../utils/propTypes/componentProp';
import { CALYPSO, GYPSUM, OLAF } from 'HubStyleTokens/colors';
import UIButton from '../button/UIButton';
import { callIfPossible } from '../core/Functions';
import Controllable from '../decorators/Controllable';
import SyntheticEvent from '../core/SyntheticEvent';
import lazyEval from '../utils/lazyEval';
import createLazyPropType from '../utils/propTypes/createLazyPropType';
import { fontsLoaded, fontsLoadedPromise } from '../utils/Fonts';
import { hidden } from '../utils/propTypes/decorators';
import UIOverhang from '../scroll/UIOverhang';
import UIIcon from '../icon/UIIcon';
import memoizeOne from 'react-utils/memoizeOne';
import { requestSchedulerCallback, cancelSchedulerCallback } from '../utils/Timers';
import { mergeRefs } from '../utils/mergeRefs';
import refObject from '../utils/propTypes/refObject';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const EXPANDABLE_TEXT_HEIGHT_OFFSET = 40;
const ExpandableTextOverhang = styled(UIOverhang).withConfig({
  displayName: "UIExpandableText__ExpandableTextOverhang",
  componentId: "sc-1q7cupc-0"
})(["", ";"], props => props.scrollable && `
    /* Compensate for border on scroll container */
    right: 1px;
    bottom: 1px;
    left: 1px;
  `);
ExpandableTextOverhang.propTypes = Object.assign({}, UIOverhang.propTypes, {
  scrollable: PropTypes.bool.isRequired
});
ExpandableTextOverhang.defaultProps = UIOverhang.defaultProps;
const getStyle = computedMaxHeight => ({
  maxHeight: computedMaxHeight
});

// If a rems value exists, convert it to px and return it; otherwise, return the px value.
const pxValue = (px, rems) => rems != null ? rems * 16 : px;
class UIExpandableText extends Component {
  constructor(props) {
    super(props);
    this.delayedFitToMaxLines = () => {
      cancelSchedulerCallback(this._timeout);
      this._timeout = requestSchedulerCallback(this.fitToMaxLines);
    };
    this.getButtonHeight = buttonEl => {
      if (!buttonEl) return 0;
      const styles = window.getComputedStyle(buttonEl);
      const margin = parseFloat(styles['marginTop']) + parseFloat(styles['marginBottom']);
      return Math.ceil(buttonEl.offsetHeight + margin);
    };
    this.fitToMaxLines = () => {
      if (!this._el) return;
      const {
        maxHeight,
        maxHeightRems
      } = this.props;
      const {
        collapsible,
        contentHeight
      } = this.state;
      const buttonHeight = this.getButtonHeight(this._containerEl.children[1]);
      const scrollHeight = this._el.scrollHeight + buttonHeight;
      if (contentHeight !== scrollHeight) {
        this.setState({
          contentHeight: scrollHeight
        });
      }
      const contentOverflows = scrollHeight > pxValue(maxHeight, maxHeightRems);
      if (collapsible !== contentOverflows) {
        this.setState({
          collapsible: contentOverflows
        });
      }
    };
    this.handleExpandButtonClick = evt => {
      const {
        onExpandButtonClick,
        onExpandedChange
      } = this.props;
      callIfPossible(onExpandButtonClick, evt);
      onExpandedChange(SyntheticEvent(true));
    };
    this.handleCollapseButtonClick = evt => {
      const {
        onCollapseButtonClick,
        onExpandedChange
      } = this.props;
      callIfPossible(onCollapseButtonClick, evt);

      // Set explicit max-height (removed by handleTransitionEnd) for transition
      const textContainerEl = this._containerEl;
      textContainerEl.style.maxHeight = `${textContainerEl.scrollHeight}px`;
      onExpandedChange(SyntheticEvent(false));
    };
    this.handleTextWrapperFocus = evt => {
      const {
        expandOnFocus,
        onExpandedChange,
        toggleable
      } = this.props;
      if (expandOnFocus && toggleable) {
        // Avoid expanding on focus if the user clicked a link or other extraneous element.
        if (!isPageInKeyboardMode() && !/TEXTAREA|INPUT/.test(evt.target.tagName)) return;
        onExpandedChange(SyntheticEvent(true));
      }
    };
    this.handleTextWrapperTransitionEnd = evt => {
      // After the expand transition, remove max-height in case the content grows/shrinks
      const textWrapperEl = this._el;
      if (evt.target === textWrapperEl && evt.propertyName === 'max-height') {
        const {
          expanded,
          fullyExpanded
        } = this.state;
        if (expanded && !fullyExpanded) {
          this.setState({
            fullyExpanded: true
          });
        }
      }
    };
    this.textWrapperRefCallback = ref => {
      this._el = ref;
    };
    this.textContainerRefCallback = ref => {
      this._containerEl = ref;
    };
    this.state = {
      contentHeight: null,
      // How tall is the content?
      collapsible: false,
      // Does the content exceed our max height?
      fullyExpanded: false // Did the CSS transition from collapsed to expanded complete?
    };
    this._getStyle = memoizeOne(getStyle);
  }
  componentDidMount() {
    this.delayedFitToMaxLines();
    this.startMutationObserver();
    addEventListener('resize', this.delayedFitToMaxLines);
    if (this._el) this._el.addEventListener('load', this.delayedFitToMaxLines, true); // #5980: need to add this handler manually
    if (!fontsLoaded()) fontsLoadedPromise.then(this.delayedFitToMaxLines);
  }
  componentDidUpdate(prevProps, prevState) {
    this.stopMutationObserver();

    // ⚠️ This condition prevents infinite loops! ⚠️
    if (prevState.contentHeight === this.state.contentHeight && prevState.collapsible === this.state.collapsible) {
      this.delayedFitToMaxLines();
    }
    if (prevState.collapsible !== this.state.collapsible) {
      callIfPossible(this.props.onCollapsibleChange, this.state.collapsible);
    }
    this.startMutationObserver();
  }
  componentWillUnmount() {
    this.stopMutationObserver();
    cancelSchedulerCallback(this._timeout);
    removeEventListener('resize', this.delayedFitToMaxLines);
    if (this._el && this._el.removeEventListener) this._el.removeEventListener('load', this.delayedFitToMaxLines, true);
  }
  startMutationObserver() {
    if (!this._el) return;

    // When the content changes, recalculate our size
    this._observer = new MutationObserver(this.delayedFitToMaxLines);
    this._observer.observe(this._el, {
      attributes: true,
      childList: true,
      subtree: true
    });
  }
  stopMutationObserver() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }
  render() {
    const {
      buttonAlign,
      children,
      className,
      collapseButtonText,
      expandButtonText,
      expanded,
      fadeOutHeight,
      fadeOutHeightRems,
      gradientColor,
      hideButtonCaret,
      maxHeight,
      maxHeightRems,
      onCollapseButtonClick,
      onExpandButtonClick,
      Overhang,
      scrollable,
      toggleable,
      use,
      domNodeRef
    } = this.props;
    const {
      collapsible,
      contentHeight,
      fullyExpanded
    } = this.state;
    const showToggleButton = toggleable && collapsible;
    const hasBottomToggleButton = !scrollable && showToggleButton;
    const computedClassName = classNames(className, 'private-expandable-text', scrollable && 'private-expandable-text--scrollable', hasBottomToggleButton && 'private-expandable-text__overhang-wrapper');
    const computedContainerClassName = 'private-expandable-text__container' + (!hasBottomToggleButton ? " private-expandable-text__overhang-wrapper" : "");
    const computedButtonClassName = classNames('private-expandable-text__toggle-button', scrollable ? 'align-right' : buttonAlign === 'left' && 'align-left');
    const computedMaxHeight = expanded ? contentHeight : pxValue(maxHeight, maxHeightRems);
    const renderedButton = showToggleButton ? /*#__PURE__*/_jsxs(UIButton, {
      "aria-hidden": expanded ? !onCollapseButtonClick : !onExpandButtonClick,
      className: computedButtonClassName,
      onClick: expanded ? this.handleCollapseButtonClick : this.handleExpandButtonClick,
      use: "link",
      children: [scrollable || hideButtonCaret ? null : /*#__PURE__*/_jsx(UIIcon, {
        color: CALYPSO,
        name: expanded ? 'up' : 'down'
      }), lazyEval(expanded ? collapseButtonText : expandButtonText)]
    }) : null;
    const defaultGradientColor = use === 'on-dark' ? GYPSUM : OLAF;
    return /*#__PURE__*/_jsxs("div", {
      className: computedContainerClassName,
      "data-collapsible": collapsible,
      ref: mergeRefs([this.textContainerRefCallback, domNodeRef]),
      style: fullyExpanded ? null : this._getStyle(computedMaxHeight),
      children: [scrollable && renderedButton, /*#__PURE__*/_jsxs("div", {
        className: computedClassName,
        onFocus: this.handleTextWrapperFocus,
        onTransitionEnd: this.handleTextWrapperTransitionEnd,
        ref: this.textWrapperRefCallback,
        children: [children, collapsible && !expanded ? /*#__PURE__*/_jsx(Overhang, {
          gradientColor: gradientColor != null ? gradientColor : defaultGradientColor,
          scrollable: scrollable,
          side: "bottom",
          size: pxValue(fadeOutHeight, fadeOutHeightRems)
        }) : null]
      }), !scrollable && renderedButton]
    });
  }
}
UIExpandableText.displayName = 'UIExpandableText';
UIExpandableText.defaultProps = {
  buttonAlign: 'center',
  collapseButtonText: () => I18n.text('salesUI.UIExpandableText.collapseButtonText'),
  expandButtonText: () => I18n.text('salesUI.UIExpandableText.expandButtonText'),
  expanded: false,
  expandOnFocus: true,
  fadeOutHeight: 32,
  hideButtonCaret: false,
  maxHeight: 120,
  Overhang: ExpandableTextOverhang,
  scrollable: false,
  toggleable: true,
  use: 'default'
};
UIExpandableText.propTypes = {
  buttonAlign: PropTypes.oneOf(['left', 'center']).isRequired,
  children: PropTypes.node.isRequired,
  collapseButtonText: createLazyPropType(PropTypes.node).isRequired,
  expandButtonText: createLazyPropType(PropTypes.node).isRequired,
  expanded: PropTypes.bool,
  expandOnFocus: PropTypes.bool.isRequired,
  fadeOutHeight: PropTypes.number.isRequired,
  fadeOutHeightRems: hidden(PropTypes.number),
  gradientColor: PropTypes.string,
  hideButtonCaret: PropTypes.bool.isRequired,
  /** Sets the height of the expandable text. To preserve visual consistency, import and add `EXPANDABLE_TEXT_HEIGHT_OFFSET` (`40px`) to the prop's value. */
  maxHeight: PropTypes.number.isRequired,
  maxHeightRems: hidden(PropTypes.number),
  onCollapsibleChange: hidden(PropTypes.func),
  onCollapseButtonClick: PropTypes.func,
  onExpandButtonClick: PropTypes.func,
  onExpandedChange: PropTypes.func,
  Overhang: getComponentPropType(ExpandableTextOverhang),
  scrollable: PropTypes.bool,
  toggleable: PropTypes.bool,
  use: PropTypes.oneOf(['default', 'on-dark']),
  domNodeRef: refObject
};
export default Controllable(UIExpandableText, ['expanded']);